import React, { useEffect } from 'react';
import Layout from '../layout/Layout';
import { usePageContent } from '../../context/ContentContext';

const KnitStore = () => {
    const { t, g } = usePageContent('knitStore');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 leading-[60pt] tracking-[-0.02em] text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-12 text-primary-dark-red">
                            {t('intro.title')}
                        </h2>
                        <div className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em]">
                            {t('intro.paragraphs').map((paragraph, index) => (
                                <p key={index} className="mb-6">{paragraph}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Yarn Categories */}
            <section className="py-24 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('collection.title')}
                    </h2>

                    <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                        {t('collection.cards').map((card, index) => (
                            <div key={index} className="bg-white p-8 rounded-lg shadow-sm">
                                <div className="text-4xl mb-4 text-center">{card.emoji}</div>
                                <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red text-center">
                                    {card.title}
                                </h3>
                                <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-gray-700 mb-4">
                                    {card.body}
                                </p>
                                <ul className="font-poppins font-normal text-sub text-gray-600 space-y-2">
                                    {(card.bullets || []).map((bullet, bulletIndex) => (
                                        <li key={bulletIndex}>• {bullet}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* UK Spinner Partnerships */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('spinners.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-center mb-12">
                            {t('spinners.intro')}
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {t('spinners.cards').map((card, index) => (
                                <div key={index} className="bg-accent-powder-green p-8 rounded-lg">
                                    <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{card.title}</h3>
                                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-gray-700 mb-4">
                                        {card.body}
                                    </p>
                                    <p className="font-poppins font-normal text-sub text-gray-600">
                                        <strong>{t('spinners.specialtiesLabel')}</strong> {card.specialties}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Gauge Range */}
            <section className="py-24 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('gauge.title')}
                    </h2>

                    <div className="max-w-5xl mx-auto">
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-center mb-12">
                            {t('gauge.intro')}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            {t('gauge.items').map((item, index) => (
                                <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm">
                                    <div className="text-3xl font-light text-primary-red mb-2">{item.gauge}</div>
                                    <h3 className="font-poppins font-semibold text-h3 text-primary-dark-red mb-2">{item.title}</h3>
                                    <p className="font-poppins font-normal text-sub text-gray-600">
                                        {item.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Sustainability Certifications */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('certifications.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-center mb-12">
                            {t('certifications.intro')}
                        </p>

                        <div className="grid md:grid-cols-3 gap-8">
                            {t('certifications.items').map((item, index) => (
                                <div key={index} className="text-center p-6">
                                    <div className="bg-accent-powder-green w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="text-3xl">{item.emoji}</span>
                                    </div>
                                    <h3 className="font-poppins font-semibold text-h3 mb-2 text-primary-red">{item.title}</h3>
                                    <p className="font-poppins font-normal text-sub text-gray-600">
                                        {item.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-primary-dark-red text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-6">
                        {t('cta.title')}
                    </h2>
                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] max-w-2xl mx-auto mb-10">
                        {t('cta.body')}
                    </p>
                    <a
                        href={`mailto:${g('contact.email')}`}
                        className="inline-block px-8 py-4 bg-primary-red text-white hover:bg-pink-600 transition-colors duration-300 font-poppins font-semibold text-h3"
                    >
                        {t('cta.buttonText')}
                    </a>
                </div>
            </section>
        </Layout>
    );
};

export default KnitStore;
